import { ArchbaseAuthenticator, ArchbaseUser, ArchbaseAccessToken } from '@archbase/security'
import { ARCHBASE_IOC_API_TYPE } from '@archbase/data'
import type { ArchbaseRemoteApiClient } from '@archbase/data'
import { inject, injectable } from 'inversify'
import { STORAGE_KEYS } from '../AppConstants'

/**
 * Usuário do console.
 *
 * O doclenz-api não tem modelo de usuário — a autenticação é uma chave de admin só. Este
 * objeto existe porque o shell do Archbase precisa de um usuário para montar o layout, e não
 * porque haja identidade de verdade por trás. Ver README, seção "Autenticação".
 */
export class AppUser extends ArchbaseUser {
  public id: string
  public displayName: string
  public email: string
  public photo: string
  public isAdmin: boolean
  public role: string
  public permissions: string[]
  public isActive: boolean

  constructor(data: any) {
    super(data)
    this.id = data.id || 'admin'
    this.displayName = data.displayName || 'Administrador'
    this.email = data.email || ''
    this.photo = data.photo || ''
    this.isAdmin = true
    this.role = 'ADMIN'
    this.permissions = data.permissions || []
    this.isActive = true
  }

  public isAdministrator = (): boolean => true
}

/**
 * Autenticador por chave de admin.
 *
 * Não há `/auth/login`, token de acesso nem refresh no doclenz-api. O que existe é o header
 * `X-Admin-Key` conferido pelo `AdminController`. Então "logar" aqui é provar que a chave vale,
 * chamando um endpoint protegido e vendo se ele responde 200 — hoje `GET /v1/admin/keys`, que é
 * de leitura e não tem efeito colateral.
 *
 * A chave é devolvida como se fosse um access token para o `ArchbaseAuthenticationManager` seguir
 * funcionando; o interceptor registrado no ContainerIOC é quem a converte de volta em
 * `X-Admin-Key` nas requisições.
 *
 * **Trocar para JWT depois é reimplementar só esta classe** — nada mais no console conhece o
 * formato da credencial.
 */
@injectable()
export class AppAuthenticator implements ArchbaseAuthenticator {
  private client: ArchbaseRemoteApiClient

  constructor(@inject(ARCHBASE_IOC_API_TYPE.ApiClient) client: ArchbaseRemoteApiClient) {
    this.client = client
  }

  /**
   * @param _username ignorado — não há usuário, só a chave
   * @param password a chave de admin
   */
  public async login(_username: string, password: string): Promise<ArchbaseAccessToken> {
    const adminKey = (password || '').trim()
    if (!adminKey) {
      throw new Error('Informe a chave de admin.')
    }

    try {
      await this.client.get('/v1/admin/keys', { 'X-Admin-Key': adminKey }, true)
    } catch (error: any) {
      const status = error?.response?.status
      if (status === 401) {
        throw new Error('Chave de admin inválida.')
      }
      throw new Error(
        `Não foi possível falar com a API do doclenz${status ? ` (HTTP ${status})` : ''}.`
      )
    }

    localStorage.setItem(STORAGE_KEYS.ADMIN_KEY, adminKey)

    return {
      access_token: adminKey,
      refresh_token: adminKey,
      token_type: 'X-Admin-Key',
      // A chave não expira sozinha; só deixa de valer quando alguém a troca no servidor.
      expires_in: 60 * 60 * 24 * 365,
      scope: 'admin',
    } as ArchbaseAccessToken
  }

  /** Não há refresh: a chave é estática. Devolver a mesma mantém a sessão viva. */
  public async refreshToken(refresh_token: string): Promise<ArchbaseAccessToken> {
    return {
      access_token: refresh_token,
      refresh_token,
      token_type: 'X-Admin-Key',
      expires_in: 60 * 60 * 24 * 365,
      scope: 'admin',
    } as ArchbaseAccessToken
  }

  public async logout(): Promise<void> {
    localStorage.removeItem(STORAGE_KEYS.ADMIN_KEY)
  }

  sendResetPasswordEmail(_email: string): Promise<void> {
    return Promise.reject(
      new Error('Não se aplica: o console autentica por chave de admin, não por usuário.')
    )
  }

  resetPassword(_email: string, _token: string, _newPassword: string): Promise<void> {
    return Promise.reject(
      new Error('Não se aplica: para trocar a chave, altere DOCLENZ_ADMIN_KEY e reinicie a API.')
    )
  }
}

/** A chave de admin em uso, ou null. */
export function currentAdminKey(): string | null {
  return localStorage.getItem(STORAGE_KEYS.ADMIN_KEY)
}
